"use client";

import classNames from "classnames";
import {
  CSSProperties,
  useEffect,
  useId,
  useMemo,
  useRef,
  type JSX,
} from "react";
import {
  Button,
  Typography,
  ListLayout,
} from "@cappelendamm/design-system/react";
import { slugify } from "@cappelendamm/utils/slugify";
import { Link } from "@cappelendamm/design-system/react";
import NextLink from "next/link";
import {
  IndexDocument,
  isIndexedProductDocument,
} from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { ListingItem } from "./item-listings/ListingItem";
import { ListingItemConfig } from "./item-listings/types";
import { useTrackEvent } from "@/tracking/TrackingContext";
import { EcommerceEvent } from "@/tracking/e-commerce";
import { LOAD_MORE_BATCH_SIZE } from "../constants";
import { useItemListing } from "./ItemListingContext";

const MANDATORY_ROWS = ["image", "title", "author"];

export type ResultListProps = {
  title?: string;
  internalName: string;
  highlightedItems?: IndexDocument[] | null;
  highlightedOnly?: boolean | null;
  variant?: "faceted" | "grid" | "carousel";
  listAction?: {
    label?: string | null;
    href: string;
  };
  itemArticle?: ListingItemConfig;
};

export function ResultList({
  internalName,
  className,
  variant = "faceted",
  highlightedItems,
  highlightedOnly,
  title,
  itemArticle = {
    fields: { formatMeta: true, series: true, price: true },
    tags: {
      publicationDate: false,
      highlighted: true,
      informational: true,
      promoted: true,
    },
  },
  listAction,
  ...rest
}: ResultListProps &
  Omit<JSX.IntrinsicElements["div"], keyof ResultListProps>) {
  const trackEvent = useTrackEvent();
  const {
    loadMore,
    results: paginatedItems,
    search,
    isLastPage,
    isLoading,
  } = useItemListing();
  const listTrackingData = useMemo(
    () => ({
      item_list_id: slugify([internalName]),
      item_list_name: title ?? internalName,
    }),
    [internalName, title],
  );
  const sortedItems = useMemo<IndexDocument[]>(() => {
    if (highlightedOnly && highlightedItems) return highlightedItems;
    if (highlightedOnly && !highlightedItems) return [];
    if (!highlightedItems) return paginatedItems;
    const highlightedObjectIds = new Set(
      highlightedItems?.map(({ objectID }) => objectID),
    );

    const sorted: IndexDocument[] = [
      ...highlightedItems,
      ...paginatedItems.filter(
        (item) => !highlightedObjectIds.has(item.objectID),
      ),
    ];

    return sorted;
  }, [paginatedItems, highlightedItems, highlightedOnly]);

  const renderedItems = useMemo<IndexDocument[]>(() => {
    if (variant !== "carousel") return sortedItems;
    const maxItems = Math.min(
      highlightedItems && highlightedItems.length > 0
        ? highlightedItems.length
        : 10,
      sortedItems.length,
    );
    return sortedItems.slice(0, maxItems);
  }, [highlightedItems, sortedItems, variant]);

  const id = useId();
  const trackedItems = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (
      renderedItems.find(
        (item) =>
          !trackedItems.current || !trackedItems.current.has(item.objectID),
      )
    ) {
      trackEvent(
        EcommerceEvent({
          eventName: "view_item_list",
          ...listTrackingData,
          skus: renderedItems.map((item) => item.objectID),
        }),
      );
      trackedItems.current = new Set(
        renderedItems.map((item) => item.objectID),
      );
    }
  }, [renderedItems, listTrackingData, trackEvent]);

  const rowCount = Object.values(itemArticle.fields ?? {}).reduce(
    (acc, enabled) => (enabled ? acc + 1 : acc),
    MANDATORY_ROWS.length,
  );

  if (renderedItems.length === 0) return null;
  return (
    <div
      className={classNames(
        className,
        "grid h-fit grid-cols-[1fr_auto] items-center justify-center gap-x-4",
        {
          "[--list-row-count:7]": rowCount === 7,
          "[--list-row-count:6]": rowCount === 6,
          "[--list-row-count:5]": rowCount === 5,
          "[--list-row-count:4]": rowCount === 4,
        },
      )}
      aria-roledescription={variant === "carousel" ? "carousel" : undefined}
      data-insights-query-id={internalName}
      {...rest}
    >
      {variant !== "faceted" ? (
        <div className="flex">
          {title ? (
            <Typography as="h2" variant="title-lg">
              {title}
            </Typography>
          ) : null}
          {listAction?.href ? (
            <Link
              as={NextLink}
              href={listAction.href}
              variant="inline"
              className="ml-auto text-base"
            >
              {listAction.label}
            </Link>
          ) : null}
        </div>
      ) : null}

      <ListLayout
        id={id}
        variant={variant === "carousel" ? "carousel" : "grid"}
        carousel={{
          config: { count: renderedItems.length },
          controls: {
            hide: renderedItems.length < 5,
            className:
              "[--cvr-stack-carousel--controls-height:--spacing(64)] top-[--spacing(12)]",
          },
        }}
        as="ol"
        className={classNames(
          "col-span-full mt-4 grid-rows-(--list-row-count) [--cvr-stack-columns:2] @md:[--cvr-stack-columns:3] @lg:[--cvr-stack-columns:4]",
          { "grid-flow-dense": variant === "faceted" },
        )}
      >
        {renderedItems.map((hit, index) => {
          const batchIndex =
            (index % (search.limit ?? LOAD_MORE_BATCH_SIZE)) + 1;
          const trackSelection = () => {
            if (isIndexedProductDocument({ itemType: hit.itemType }))
              trackEvent(
                EcommerceEvent({
                  eventName: "select_item",
                  skus: [hit.objectID],
                  index,
                  ...listTrackingData,
                }),
              );
          };
          return (
            <li
              key={hit.objectID}
              style={{ "--sibling-index": batchIndex } as CSSProperties}
              className={classNames(
                "row-span-(--list-row-count) mb-4 grid cvr-stagger-siblings grid-rows-subgrid",
                {
                  "col-span-2":
                    hit.itemType === "product-bundle" ||
                    hit.itemType === "literary-work" ||
                    hit.itemType === "series",
                  "col-span-full": hit.itemType === "subscription",
                },
              )}
              onClick={trackSelection}
              onAuxClick={trackSelection}
              data-insights-object-id={hit.objectID}
              data-insights-position={index}
            >
              <ListingItem item={hit} config={itemArticle} />
            </li>
          );
        })}
      </ListLayout>
      {!isLastPage &&
      (variant === "faceted" || variant === "grid") &&
      !highlightedOnly ? (
        <Button
          variant="primary"
          onClick={loadMore}
          disabled={isLoading}
          className="col-span-full mt-4 w-fit justify-self-center"
        >
          Last inn flere
        </Button>
      ) : null}
    </div>
  );
}
